import React, { Fragment, useMemo } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import { Heading2 } from '@awarego-ui/components';
import ThemedLinkStep from '../../components/themed/ThemedLinkStep';
import classes from 'classnames';
import ProfileSettingsContainer from '../settings/ProfileSettings';
import CompaniesList from './CompaniesList';
import PartnerResourcesContainer from './PartnerResources';
import PartnerAdmins from './PartnerAdmins';
import PartnerApiKeys from './PartnerApiKeys';
import PartnerWebhooks from './PartnerWebhooks';
import PartnerBrandingSettings from './PartnerBrandingSettings';
import PartnerDistributors from './PartnerDistributors';
import { Heading1, Block, TextDefault } from '@awarego-ui/components';
import { Drawer, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { observer } from 'mobx-react';
import { useStores } from '../../hooks/use-stores';

/**
 * Partner-level settings shell with sidebar, similar to company Settings.
 *
 * Routes under /partner/:partnerId/*:
 * - /partner/:partnerId/companies        -> Companies list (current page)
 * - /partner/:partnerId/profile          -> My profile (reuses ProfileSettings)
 * - /partner/:partnerId/admins           -> Partner admins (placeholder)
 * - /partner/:partnerId/settings         -> Partner branding/settings (placeholder)
 * - /partner/:partnerId/apikeys          -> Partner API keys (placeholder)
 * - /partner/:partnerId/resources/*      -> Partner resources (existing)
 * - /partner/:partnerId/payments         -> Payments (existing)
 */
const PartnerSettings = () => {
    const location = useLocation();
    const { partnerId } = useParams();
    const { authStore, partnerStore } = useStores();
    const [showMobileMenu, setShowMobileMenu] = React.useState(false);
    const [collapsed, setCollapsed] = React.useState(false);
    const [drawerOpen, setDrawerOpen] = React.useState(false);

    const basePath = `/partner/${partnerId}`;

    // Partner-scoped visibility, independent of the company's ui.webhooks_hidden.
    React.useEffect(() => {
        if (partnerId) partnerStore.loadSettings(partnerId);
    }, [partnerId, partnerStore]);

    const showWebhooks = !(
        partnerStore.uiSettings?.['ui.partner_webhooks_hidden'] === '1'
    );
    const showDistributors = partnerStore.canManageDistributors;

    const partnerName = useMemo(() => {
        const partner = authStore.currentUser?.superForAccounts?.find(
            (x) => x.id === Number(partnerId)
        );
        return partner?.name || 'Partner';
    }, [partnerId, authStore.currentUser]);

    const collapseStorageKey = React.useMemo(() => {
        if (partnerId) {
            return `settings_sidebar_collapsed_partner_${partnerId}`;
        }
        return 'settings_sidebar_collapsed_partner_default';
    }, [partnerId]);

    React.useEffect(() => {
        try {
            const stored = window.localStorage.getItem(collapseStorageKey);
            if (stored === '1') {
                setCollapsed(true);
            }
        } catch {
            // ignore storage errors
        }
    }, [collapseStorageKey]);

    const toggleCollapsed = (event) => {
        if (event && event.stopPropagation) {
            event.stopPropagation();
        }
        setCollapsed((prev) => {
            const next = !prev;
            try {
                window.localStorage.setItem(
                    collapseStorageKey,
                    next ? '1' : '0'
                );
            } catch {
                // ignore storage errors
            }
            if (!next) {
                // If unpinning, close drawer
                setDrawerOpen(false);
            }
            return next;
        });
    };

    const handleBurgerClick = () => {
        setDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setDrawerOpen(false);
    };

    const showLessonMenu = () => setShowMobileMenu(true);
    const hideLessonMenu = () => setShowMobileMenu(false);

    const isActive = (pathPrefix) =>
        location.pathname === pathPrefix ||
        location.pathname.startsWith(pathPrefix + '/');

    return (
        <Fragment>
            {/* Fixed burger button when collapsed */}
            {collapsed && (
                <IconButton
                    onClick={handleBurgerClick}
                    sx={{
                        position: 'fixed',
                        left: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 1000,
                        backgroundColor: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        '&:hover': {
                            backgroundColor: '#f5f5f5',
                        },
                    }}
                >
                    <MenuIcon />
                </IconButton>
            )}

            {/* Drawer menu when collapsed */}
            <Drawer
                anchor="left"
                open={drawerOpen && collapsed}
                onClose={handleDrawerClose}
                PaperProps={{
                    sx: {
                        width: 280,
                        paddingTop: 2,
                    },
                }}
            >
                <div style={{ padding: '16px' }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '16px',
                        }}
                    >
                        <Heading2 displayBlock>Settings</Heading2>
                        <IconButton
                            onClick={toggleCollapsed}
                            size="small"
                            title="Pin sidebar"
                        >
                            {collapsed ? (
                                <PushPinOutlinedIcon />
                            ) : (
                                <PushPinIcon />
                            )}
                        </IconButton>
                    </div>
                    <ThemedLinkStep
                        active={
                            location.pathname === basePath ||
                            isActive(`${basePath}/profile`)
                                ? 'active'
                                : 0
                        }
                        to={`${basePath}/profile`}
                        text="My profile"
                        onClick={handleDrawerClose}
                    />

                    <hr />

                    <ThemedLinkStep
                        active={
                            isActive(`${basePath}/admins`) ? 'active' : 0
                        }
                        to={`${basePath}/admins`}
                        text="Admins"
                        onClick={handleDrawerClose}
                    />

                    <ThemedLinkStep
                        active={
                            isActive(`${basePath}/companies`) ? 'active' : 0
                        }
                        to={`${basePath}/companies`}
                        text="Companies"
                        onClick={handleDrawerClose}
                    />

                    {showDistributors && (
                        <ThemedLinkStep
                            active={
                                isActive(`${basePath}/distributors`)
                                    ? 'active'
                                    : 0
                            }
                            to={`${basePath}/distributors`}
                            text="Distributors"
                            onClick={handleDrawerClose}
                        />
                    )}

                    <ThemedLinkStep
                        active={
                            isActive(`${basePath}/settings`) ? 'active' : 0
                        }
                        to={`${basePath}/settings`}
                        text="Settings"
                        onClick={handleDrawerClose}
                    />
                    <ThemedLinkStep
                        active={
                            isActive(`${basePath}/apikeys`) ? 'active' : 0
                        }
                        to={`${basePath}/apikeys`}
                        text="API Keys"
                        onClick={handleDrawerClose}
                    />
                    {showWebhooks && (
                        <ThemedLinkStep
                            active={
                                isActive(`${basePath}/webhooks`) ? 'active' : 0
                            }
                            to={`${basePath}/webhooks`}
                            text="Webhooks"
                            onClick={handleDrawerClose}
                        />
                    )}
                </div>
            </Drawer>

            <div className="d-flex">
                <div
                    className={classes('sidebar-mobile-menu ', {
                        show: !showMobileMenu,
                    })}
                    onClick={showLessonMenu}
                ></div>
                <div
                    className={classes('sidebar-mobile-menu close', {
                        show: showMobileMenu,
                    })}
                    onClick={hideLessonMenu}
                ></div>

                {!collapsed && (
                    <div
                        className={classes(
                            'block sidebar-steps sidebar sidebar-links ',
                            { open: showMobileMenu }
                        )}
                        onClick={hideLessonMenu}
                    >
                        <div className="sidebar-inner">
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginBottom: '16px',
                                    paddingLeft: '34px',
                                    paddingRight: '16px',
                                }}
                            >
                                <Heading2
                                    mb={16}
                                    mt={showMobileMenu ? 16 : 8}
                                    displayBlock
                                >
                                    Settings
                                </Heading2>
                                <IconButton
                                    onClick={toggleCollapsed}
                                    size="small"
                                    title="Collapse sidebar"
                                >
                                    <PushPinIcon />
                                </IconButton>
                            </div>
                            <ThemedLinkStep
                                active={
                                    location.pathname === basePath ||
                                    isActive(`${basePath}/profile`)
                                        ? 'active'
                                        : 0
                                }
                                to={`${basePath}/profile`}
                                text="My profile"
                            />

                            <hr />

                            <ThemedLinkStep
                                active={
                                    isActive(`${basePath}/admins`)
                                        ? 'active'
                                        : 0
                                }
                                to={`${basePath}/admins`}
                                text="Admins"
                            />

                            <ThemedLinkStep
                                active={
                                    isActive(`${basePath}/companies`)
                                        ? 'active'
                                        : 0
                                }
                                to={`${basePath}/companies`}
                                text="Companies"
                            />

                            {showDistributors && (
                                <ThemedLinkStep
                                    active={
                                        isActive(`${basePath}/distributors`)
                                            ? 'active'
                                            : 0
                                    }
                                    to={`${basePath}/distributors`}
                                    text="Distributors"
                                />
                            )}

                            <ThemedLinkStep
                                active={
                                    isActive(`${basePath}/settings`)
                                        ? 'active'
                                        : 0
                                }
                                to={`${basePath}/settings`}
                                text="Settings"
                            />
                            <ThemedLinkStep
                                active={
                                    isActive(`${basePath}/apikeys`)
                                        ? 'active'
                                        : 0
                                }
                                to={`${basePath}/apikeys`}
                                text="API Keys"
                            />
                            {showWebhooks && (
                                <ThemedLinkStep
                                    active={
                                        isActive(`${basePath}/webhooks`)
                                            ? 'active'
                                            : 0
                                    }
                                    to={`${basePath}/webhooks`}
                                    text="Webhooks"
                                />
                            )}
                        </div>
                    </div>
                )}

                <div
                    className={classes('d-flex flex-column', {
                        'fullwidth': !collapsed,
                    })}
                    style={collapsed ? { width: '100%' } : {}}
                >
                    <Row>
                        <Col md="12" xs="12">
                            <Routes>
                                <Route
                                    path="/"
                                    element={
                                        <Navigate
                                            to={`${basePath}/companies`}
                                            replace={true}
                                        />
                                    }
                                />

                                <Route
                                    path="profile"
                                    element={<ProfileSettingsContainer />}
                                />

                                <Route
                                    path="companies/*"
                                    element={
                                        <Container className="wide campaigns">
                                            <CompaniesList />
                                        </Container>
                                    }
                                />

                                <Route
                                    path="resources/*"
                                    element={
                                        <Container className="manager-content campaigns wide">
                                            <PartnerResourcesContainer />
                                        </Container>
                                    }
                                />
                                <Route
                                    path="payments"
                                    element={
                                        <Container className="manager-content list-table">
                                            <Heading1 displayBlock mt={24}>
                                                Payments
                                            </Heading1>
                                        </Container>
                                    }
                                />

                                <Route
                                    path="admins"
                                    element={
                                        <Container className="manager-content campaigns wide">
                                            <PartnerAdmins />
                                        </Container>
                                    }
                                />

                                <Route
                                    path="distributors"
                                    element={
                                        <Container className="manager-content campaigns wide">
                                            <PartnerDistributors />
                                        </Container>
                                    }
                                />

                                <Route
                                    path="settings"
                                    element={
                                        <Container className="manager-content campaigns wide">
                                            <PartnerBrandingSettings />
                                        </Container>
                                    }
                                />

                                <Route
                                    path="apikeys"
                                    element={
                                        <Container className="manager-content campaigns wide">
                                            <PartnerApiKeys />
                                        </Container>
                                    }
                                />

                                {showWebhooks && (
                                    <Route
                                        path="webhooks"
                                        element={
                                            <Container className="manager-content campaigns wide">
                                                <PartnerWebhooks />
                                            </Container>
                                        }
                                    />
                                )}

                                <Route
                                    path="*"
                                    element={
                                        <Navigate
                                            to={`${basePath}/companies`}
                                            replace={true}
                                        />
                                    }
                                />
                            </Routes>
                        </Col>
                    </Row>
                </div>
            </div>
        </Fragment>
    );
};

export default observer(PartnerSettings);

